/**
 * EcountSyncView — /inventory/stocktake  실사 탭(이카운트 동기화)
 *
 * 이카운트 내보내기 자료(품목등록 · 재고현황 · 구매현황)를 자료 · 파일 → 검토 → 반영. 아래에 업로드 내역 · 연결 안 된 품목.
 * 단계는 저장하지 않는다 — preview · report 의 유무가 단계다. VOCABULARY "이카운트 동기화 — 실사 탭".
 *
 * 분해: ecount-sync/types.ts(상태 · 화면 말) · ecount-sync/render.ts(그리기, 순수) · 이 파일(핸들러 + 로드 + 부분 재렌더).
 *
 * 2 시그니처 → 3 몸통(이 커밋).
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import { findMenuPlace, menuHeading, renderMenuTabs } from '../components/menu';
import { assert } from '../lib/assert';
import { SHEET_WORD } from './ecount-sync/types';
import type { EcountSyncData } from './ecount-sync/types';
import { renderPreview, renderSteps, renderUnlinked, renderUploadForm, renderUploads } from './ecount-sync/render';

const TAB_HREF = '/inventory/stocktake';
const UPLOADS_LIMIT = 20;

export class EcountSyncView extends SolaView<EcountSyncData> {
  override events = {
    click: {
      '@es-sheet':       'handleSheetPick',   // 자료 세그먼트
      '@es-upload':      'handleUpload',      // [검토] — 폼을 읽어 올리고 preview 로
      '@es-reset':       'handleReset',       // [다시 고르기] — preview · report 를 비운다
      '@es-apply':       'handleApply',       // [반영]
      '@es-open-upload': 'handleOpenUpload',  // 내역 한 줄 → 그 업로드의 미리보기
      '@es-link':        'handleLink',        // 줄의 product id 를 읽어 잇기
      '@es-empty-price-csv': 'handleEmptyPriceCsv',   // [빈 단가 줄 CSV] — 내려받기
    },
    change: {
      '@es-exported-at':  'handleExportedAt',
      '@es-file':         'handleFile',
      '@es-slip-file': 'handleSlipFile',
    },
  };
  // dragover · drop 은 events 맵에 안 둔다 — Sola 가 핸들러를 microtask 로 미뤄 preventDefault 가 늦고 브라우저가 파일을 열어 버린다. onMount 에서 직접 단다(attachDropzones)

  constructor() {
    super();
    this.data = {
      form: { sheet: 'item', exportedAt: '', file: null, slipFile: null },
      preview: null, report: null, uploads: [], unlinked: [], loading: true, error: '',
    };
  }

  override getTitle(): string {
    return '실사 · 조정 — HCLTech';
  }

  override onMount(): void {
    this.attachDropzones();
    void this.loadAll();
  }

  render(): HTMLElement {
    const place = findMenuPlace(TAB_HREF);
    assert(place !== null, `실사 탭이 메뉴에 없다: ${TAB_HREF} — MENU_GROUPS 재고 › 실사 · 설정의 탭에 있어야 한다`);
    return htmlSolaWithInstance(this)`
      <div class="page page-ecount-sync">
        <header class="page-header">
          <h1>${menuHeading(place)}</h1>
          ${renderMenuTabs(place)}
        </header>
        <section class="page-body es-body">${this.renderBody()}</section>
      </div>`;
  }

  // ===== 핸들러 — 던지면 Sola 의 onActionError(alert) 가 받는다 =====

  /** 자료 세그먼트 — form.sheet 를 바꾸고 파일 둘을 비운다(자료마다 파일이 다르다) */
  handleSheetPick(_e: Event, el: HTMLElement): void {
    const sheet = el.dataset.sheet;
    assert(isSheet(sheet), `자료 세그먼트의 data-sheet 는 E_ECOUNT_SHEETS 중 하나여야 한다 — 세그먼트는 그 배열로 그렸다. 받은 값: ${sheet}`);

    this.data = { ...this.data, form: { ...this.data.form, sheet, file: null, slipFile: null } };
    this.rerenderBody();
  }

  /** 내보낸 날 — 상태만 바꾸고 다시 그리지 않는다(날짜 칸의 포커스를 지킨다). 검토는 누르는 순간 칸에서 다시 읽는다 */
  handleExportedAt(_e: Event, el: HTMLElement): void {
    assert(el instanceof HTMLInputElement, '내보낸 날 칸은 input 이다 — 폼이 그렇게 그렸다');
    this.data = { ...this.data, form: { ...this.data.form, exportedAt: el.value } };
  }

  /** 파일 상자(눌러서 고름) — 고른 파일은 상태에 두고 다시 그린다. 상자는 상태로 그리니 input 이 새로 만들어져도 괜찮다 */
  handleFile(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, form: { ...this.data.form, file: firstFileOrNull(el) } };
    this.rerenderBody();
  }

  handleSlipFile(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, form: { ...this.data.form, slipFile: firstFileOrNull(el) } };
    this.rerenderBody();
  }

  /** 파일 상자(떨어뜨림) — 상자의 data-file-action 이 어느 칸인지 말한다(es-file · es-slip-file). attachDropzones 가 동기로 부른다 */
  private handleDrop(e: DragEvent, el: HTMLElement): void {
    el.classList.remove('is-over');
    const file = e.dataTransfer?.files.item(0);
    if (file === null || file === undefined) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      alert('xlsx 파일만 받습니다. 이카운트에서 내보낸 엑셀을 떨어뜨리세요.');
      return;
    }
    const which = el.dataset.fileAction;
    assert(which === 'es-file' || which === 'es-slip-file', `파일 상자의 data-file-action 은 es-file · es-slip-file 중 하나다. 받은 값: ${which}`);

    this.data = { ...this.data, form: which === 'es-file' ? { ...this.data.form, file } : { ...this.data.form, slipFile: file } };
    this.rerenderBody();
  }

  /** [검토] — 폼이 비면 안내, 아니면 uploadEcountSheet → preview. 업로드 내역도 다시 받는다.
   *  내보낸 날은 누르는 순간 칸에서 읽는다 — change 가 blur 때 늦게 와 상태가 비어 있을 수 있다(2026-10-14 next 에서 검토가 안 눌림) */
  async handleUpload(): Promise<void> {
    const form = { ...this.data.form, exportedAt: this.readExportedAt() };
    this.data = { ...this.data, form };
    if (form.file === null || form.exportedAt === '') {
      alert('파일과 내보낸 날을 먼저 채우세요.');
      return;
    }
    if (form.sheet === 'purchase' && form.slipFile === null) {
      alert('구매현황은 "현황" 양식 파일도 있어야 합니다.');
      return;
    }

    const preview = await api.uploadEcountSheet({ sheet: form.sheet, exportedAt: form.exportedAt, file: form.file, slipFile: form.slipFile });
    const uploads = await api.fetchEcountUploads(UPLOADS_LIMIT, 0);
    this.data = { ...this.data, preview, report: null, uploads: uploads.rows };
    this.rerenderBody();
  }

  /** [다시 고르기] — preview · report 를 비우고 폼으로 */
  handleReset(): void {
    this.data = { ...this.data, preview: null, report: null, form: { ...this.data.form, file: null, slipFile: null } };
    this.rerenderBody();
  }

  /** [반영] — 한 번 묻고 applyEcountSync → report. 내역 · 연결 안 된 품목도 다시 받는다 */
  async handleApply(): Promise<void> {
    const { preview } = this.data;
    assert(preview !== null, '[반영] 은 검토 결과 아래에만 그려진다 — 눌렸으면 preview 가 있어야 한다');
    if (!confirm(`${SHEET_WORD[preview.sheet]} 업로드 #${preview.upload_id} 를 반영합니다. 되돌릴 수 없습니다.`)) return;

    const report = await api.applyEcountSync(preview.upload_id);
    const [uploads, unlinked] = await Promise.all([api.fetchEcountUploads(UPLOADS_LIMIT, 0), api.fetchUnlinkedEcountItems()]);
    this.data = { ...this.data, report, uploads: uploads.rows, unlinked };
    this.rerenderBody();
  }

  /** 내역 한 줄 → fetchSyncPreview → preview(반영된 것이면 report 없이 검토 결과만) */
  async handleOpenUpload(_e: Event, el: HTMLElement): Promise<void> {
    const uploadId = el.dataset.uploadId;
    assert(uploadId !== undefined && uploadId !== '', '[보기] 버튼은 data-upload-id 를 갖는다 — 내역 줄이 그렇게 그렸다');

    const preview = await api.fetchSyncPreview(uploadId);
    this.data = { ...this.data, preview, report: null };
    this.rerenderBody();
  }

  /** [빈 단가 줄 CSV] — 반영된 구매 표의 단가 없는 줄을 파일로. 상태는 안 바뀐다 */
  async handleEmptyPriceCsv(): Promise<void> {
    await api.downloadEmptyPricePurchasesCsv();
  }

  /** [잇기] — 줄의 input 에서 product id 를 읽어 setEcountItemProduct, 그 줄을 목록에서 뺀다 */
  async handleLink(_e: Event, el: HTMLElement): Promise<void> {
    const itemCode = el.dataset.itemCode;
    assert(itemCode !== undefined && itemCode !== '', '[잇기] 버튼은 data-item-code 를 갖는다 — 품목 줄이 그렇게 그렸다');
    const productId = this.readProductId(itemCode);
    if (productId === '') {
      alert('product id 를 먼저 적으세요. 제품 목록에서 찾습니다.');
      return;
    }

    await api.setEcountItemProduct(itemCode, productId);
    this.data = { ...this.data, unlinked: this.data.unlinked.filter((item) => item.item_code !== itemCode) };
    this.rerenderBody();
  }

  // ===== 로드 · 재렌더 =====

  /** 업로드 내역과 연결 안 된 품목을 같이 받는다. 어느 하나라도 실패하면 error */
  private async loadAll(): Promise<void> {
    try {
      const [uploads, unlinked] = await Promise.all([api.fetchEcountUploads(UPLOADS_LIMIT, 0), api.fetchUnlinkedEcountItems()]);
      this.data = { ...this.data, uploads: uploads.rows, unlinked, loading: false, error: '' };
    } catch (err) {
      this.data = { ...this.data, loading: false, error: err instanceof Error ? err.message : '불러오기 실패' };
    }
    this.rerenderBody();
  }

  /** 그 품목 줄의 product id 입력 글 */
  private readProductId(itemCode: string): string {
    const box = this._rootElement?.querySelector<HTMLInputElement>(`tr[data-item-code="${CSS.escape(itemCode)}"] input.es-product-id`);
    assert(box, 'product id 입력은 품목 줄과 같이 그려진다 — [잇기] 가 눌렸으면 있어야 한다');

    return box.value.trim();
  }

  private renderBody(): string {
    if (this.data.loading) return `<div class="text-muted es-note">불러오는 중…</div>`;
    if (this.data.error)   return `<div class="ce-alert">${this.data.error}</div>`;

    return `
      ${renderSteps(this.data)}
      ${renderUploadForm(this.data)}
      ${renderPreview(this.data)}
      ${renderUploads(this.data)}
      ${renderUnlinked(this.data)}`;
  }

  private rerenderBody(): void {
    this.refreshRegion('.es-body', () => this.renderBody());
  }

  /** 내보낸 날 칸의 지금 값. 폼이 안 그려져 있으면(검토 뒤) 상태 값 */
  private readExportedAt(): string {
    const input = this._rootElement?.querySelector<HTMLInputElement>('input[data-action="es-exported-at"]');
    return input ? input.value : this.data.form.exportedAt;
  }

  /** 파일 상자의 dragover · drop 을 뿌리에 직접 단다 — 동기로 preventDefault 해야 브라우저가 파일을 안 연다. 뿌리는 재렌더에도 남는다(.es-body 만 바뀜) */
  private attachDropzones(): void {
    const root = this._rootElement;
    assert(root, 'onMount 에선 뿌리 요소가 있다');
    const zoneOf = (e: Event): HTMLElement | null => (e.target instanceof Element ? e.target.closest<HTMLElement>('.es-dropzone') : null);
    root.addEventListener('dragover', (e) => {
      const zone = zoneOf(e);
      if (zone === null) return;
      e.preventDefault();
      zone.classList.add('is-over');
    });
    root.addEventListener('dragleave', (e) => { zoneOf(e)?.classList.remove('is-over'); });
    root.addEventListener('drop', (e) => {
      const zone = zoneOf(e);
      if (zone === null) return;
      e.preventDefault();
      this.handleDrop(e, zone);
    });
  }
}

/** 주소 · 세그먼트의 값은 밖에서 온 값 — 셋 중 하나인가 */
function isSheet(raw: string | undefined): raw is api.EEcountSheet {
  return api.E_ECOUNT_SHEETS.some((sheet) => sheet === raw);
}

/** 파일 칸의 첫 파일. 비우면 null */
function firstFileOrNull(el: HTMLElement): File | null {
  assert(el instanceof HTMLInputElement, '파일 칸은 input[type=file] 이다 — 폼이 그렇게 그렸다');
  const file = el.files?.item(0);
  return file === null || file === undefined ? null : file;
}
